import React, { useState } from "react";
import { 
  FiUser, 
  FiShield, 
  FiSettings, 
  FiSave, 
  FiCheckCircle,
  FiInfo,
  FiBriefcase,
  FiLock,
  FiServer
} from "react-icons/fi";

// --- Helper Components (Defined OUTSIDE) ---

const FormInput = ({ label, subLabel, ...props }) => (
  <div className="group">
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
      {label}
    </label>
    <input
      {...props}
      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#009cde] focus:border-[#009cde] focus:bg-white outline-none transition-all hover:border-[#009cde]"
    />
    {subLabel && <p className="mt-1 text-[10px] text-slate-400">{subLabel}</p>}
  </div>
);

const FormSelect = ({ label, options, ...props }) => (
  <div className="group">
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
      {label}
    </label>
    <div className="relative">
        <select
          {...props}
          className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 focus:ring-2 focus:ring-[#009cde] focus:border-[#009cde] focus:bg-white outline-none transition-all hover:border-[#009cde] appearance-none"
        >
          {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
        </div>
    </div>
  </div>
);

const TabButton = ({ id, icon: Icon, label, activeTab, setActiveTab }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-6 py-4 text-sm font-bold border-b-2 transition-all duration-200 ${
          activeTab === id 
          ? "border-[#003087] text-[#003087]" 
          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
);

const SaveButton = ({ onClick, label, loading }) => (
    <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end">
        <button 
            type="button" // Explicitly type button to prevent default form submission if inside form
            onClick={onClick}
            disabled={loading}
            className="flex items-center gap-2 px-8 py-3 bg-[#003087] text-white rounded-full font-bold text-sm hover:bg-[#001c64] shadow-lg shadow-blue-900/20 transition-all active:scale-95 disabled:opacity-70"
        >
            {loading ? "Saving..." : <><FiSave /> {label}</>}
        </button>
    </div>
);

// --- Main Component ---

const UserProfileForm = () => {
  const [activeTab, setActiveTab] = useState("basic");
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "Admin",
    lastName: "User",
    contactNumber: "9876543210",
    alternateContactNumber: "",
    customerType: "postpaid",
    gstNumber: "27ABCDE1234F1Z5",
    panNumber: "ABCDE1234F",
    address: "123 Enterprise Tower, Tech Park",
    email: "admin@enterprise.com",
    company: "Acme Corp",
    validFrom: "2025-01-01",
    validTo: "2026-01-01",
    security: "standard",
    securityActiveStatus: "active",
    accountExpiryDate: "2030-12-31",
    allowFromIP: "192.168.1.*",
    deliveryAccountType: "normal",
    trackLinkDomain: "trk.sms.com",
    creditDeductionBy: "submission",
    serviceTypes: ["transactional"],
    dltTelemarketerId: "110200001234",
    dltPrincipalEntityId: "120200005678",
    dltTelemarketerDelivery: "Yes"
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        serviceTypes: checked
          ? [...prev.serviceTypes, value]
          : prev.serviceTypes.filter((item) => item !== value)
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // --- Separate API Handlers ---

  const handleUpdateBasic = async () => {
    setIsSaving(true);
    try {
        // API Logic for Basic Info
        console.log("PUT /api/user/basic-info", {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            contactNumber: formData.contactNumber,
            company: formData.company,
            address: formData.address
        });
        
        // Simulate delay
        await new Promise(r => setTimeout(r, 800));
        alert("Basic Info updated successfully!");
    } catch (error) {
        console.error(error);
    } finally {
        setIsSaving(false);
    }
  };

  const handleUpdateSecurity = async () => {
    setIsSaving(true);
    try {
        // API Logic for Security
        console.log("PUT /api/user/security", {
            security: formData.security,
            status: formData.securityActiveStatus,
            expiry: formData.accountExpiryDate,
            ipWhitelist: formData.allowFromIP
        });

        await new Promise(r => setTimeout(r, 800));
        alert("Security settings updated successfully!");
    } catch (error) {
        console.error(error);
    } finally {
        setIsSaving(false);
    }
  };

  const handleUpdateSettings = async () => {
    setIsSaving(true);
    try {
        // API Logic for Settings/Config
        console.log("PUT /api/user/config", {
            deliveryType: formData.deliveryAccountType,
            services: formData.serviceTypes,
            dlt: {
                telemarketerId: formData.dltTelemarketerId,
                peId: formData.dltPrincipalEntityId
            }
        });

        await new Promise(r => setTimeout(r, 800));
        alert("Configuration saved successfully!");
    } catch (error) {
        console.error(error);
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-6 font-sans">
      <div className="max-w-5xl mx-auto">
        
        {/* Header (Simplified) */}
        <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#003087]">User Profile</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your account settings and preferences</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            
            {/* Tabs Header */}
            <div className="flex border-b border-slate-100 bg-white">
                <TabButton id="basic" icon={FiUser} label="Basic Info" activeTab={activeTab} setActiveTab={setActiveTab} />
                <TabButton id="security" icon={FiShield} label="Security" activeTab={activeTab} setActiveTab={setActiveTab} />
                <TabButton id="settings" icon={FiSettings} label="Configuration" activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>

            {/* Form Content Area */}
            <div className="p-8 min-h-[500px]">
                
                {/* BASIC INFO TAB */}
                {activeTab === "basic" && (
                    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} />
                            <FormInput label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} />
                            <FormInput label="Email Address" type="email" name="email" value={formData.email} onChange={handleChange} />
                            <FormInput label="Phone Number" name="contactNumber" value={formData.contactNumber} onChange={handleChange} />
                        </div>

                        <div className="border-t border-slate-100 pt-6">
                            <h3 className="text-sm font-bold text-[#003087] mb-4 flex items-center gap-2">
                                <FiBriefcase /> Company Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormInput label="Company Name" name="company" value={formData.company} onChange={handleChange} />
                                <FormSelect 
                                    label="Customer Type" 
                                    name="customerType" 
                                    value={formData.customerType} 
                                    onChange={handleChange} 
                                    options={[
                                        { value: "postpaid", label: "Post Paid" },
                                        { value: "prepaid", label: "Pre Paid" }
                                    ]} 
                                />
                                <FormInput label="GST Number" name="gstNumber" value={formData.gstNumber} onChange={handleChange} />
                                <FormInput label="PAN Number" name="panNumber" value={formData.panNumber} onChange={handleChange} />
                            </div>
                            <div className="mt-6">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                                    Registered Address
                                </label>
                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#009cde] focus:border-[#009cde] focus:bg-white outline-none transition-all hover:border-[#009cde] resize-none"
                                />
                            </div>
                        </div>

                        {/* Save Button for Basic Info */}
                        <SaveButton onClick={handleUpdateBasic} label="Update Basic Info" loading={isSaving} />
                    </div>
                )}

                {/* SECURITY TAB */}
                {activeTab === "security" && (
                    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out] max-w-3xl">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start gap-3">
                            <FiInfo className="text-[#0070ba] mt-0.5" size={18}/>
                            <div>
                                <h4 className="text-sm font-bold text-[#003087]">Security Policy</h4>
                                <p className="text-xs text-slate-600 mt-1">Changes to IP whitelisting will take effect immediately. Ensure you are connected from a allowed IP before saving.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <FormSelect 
                                label="Account Status" 
                                name="securityActiveStatus" 
                                value={formData.securityActiveStatus} 
                                onChange={handleChange}
                                options={[
                                    { value: "active", label: "Active" },
                                    { value: "inactive", label: "Inactive" },
                                    { value: "suspended", label: "Suspended" }
                                ]}
                            />
                             <FormSelect 
                                label="Security Level" 
                                name="security" 
                                value={formData.security} 
                                onChange={handleChange}
                                options={[
                                    { value: "standard", label: "Standard" },
                                    { value: "high", label: "High (2FA Required)" }
                                ]}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput label="Account Expiry" type="date" name="accountExpiryDate" value={formData.accountExpiryDate} onChange={handleChange} />
                            <FormInput 
                                label="IP Whitelist" 
                                name="allowFromIP" 
                                value={formData.allowFromIP} 
                                onChange={handleChange} 
                                subLabel="Comma separated IPs (e.g. 192.168.1.1, 10.0.0.*)"
                            />
                        </div>

                        {/* Save Button for Security */}
                        <SaveButton onClick={handleUpdateSecurity} label="Update Security Settings" loading={isSaving} />
                    </div>
                )}

                {/* SETTINGS TAB */}
                {activeTab === "settings" && (
                    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <FormSelect 
                                label="Delivery Account Type" 
                                name="deliveryAccountType" 
                                value={formData.deliveryAccountType} 
                                onChange={handleChange}
                                options={[
                                    { value: "normal", label: "Normal Delivery" },
                                    { value: "otp", label: "Priority OTP" }
                                ]}
                            />
                            <FormInput label="Tracking Domain" name="trackLinkDomain" value={formData.trackLinkDomain} onChange={handleChange} />
                        </div>

                        <div className="border-t border-slate-100 pt-6">
                             <h3 className="text-sm font-bold text-[#003087] mb-4 flex items-center gap-2">
                                <FiCheckCircle /> Enabled Services
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { label: "Promotional SMS", value: "promotional" },
                                    { label: "Transactional SMS", value: "transactional" },
                                    { label: "Auto-Approve Templates", value: "auto_approve_sms_templates" },
                                    { label: "Auto-Approve Sender IDs", value: "auto_approve_senders_ids" }
                                ].map((service) => (
                                    <label key={service.value} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-[#009cde] cursor-pointer transition-all">
                                        <input 
                                            type="checkbox" 
                                            name="serviceTypes"
                                            value={service.value}
                                            checked={formData.serviceTypes.includes(service.value)}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-[#003087] rounded focus:ring-[#003087]" 
                                        />
                                        <span className="text-sm font-medium text-slate-700">{service.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                         <div className="border-t border-slate-100 pt-6">
                             <h3 className="text-sm font-bold text-[#003087] mb-4 flex items-center gap-2">
                                <FiShield /> DLT Compliance (TMchain)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <FormInput label="Telemarketer ID" name="dltTelemarketerId" value={formData.dltTelemarketerId} onChange={handleChange} />
                                <FormInput label="Principal Entity ID" name="dltPrincipalEntityId" value={formData.dltPrincipalEntityId} onChange={handleChange} />
                                <FormInput label="Delivery ID (TMD)" name="dltTelemarketerDelivery" value={formData.dltTelemarketerDelivery} onChange={handleChange} />
                            </div>
                        </div>

                        {/* Save Button for Settings */}
                        <SaveButton onClick={handleUpdateSettings} label="Update Configuration" loading={isSaving} />
                    </div>
                )}

            </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileForm;
